import { useState } from "react";
import type { GearItem } from "../../../api/client";
import { categorize, categoryLabel } from "../../../lib/categories";
import { fmt, inUnit, unitById } from "../../../lib/units";
import { Legend } from "./Legend";
import { Pie } from "./Pie";
import type { GridLine } from "./GearListView";

/** The printable report: title, pie + legend, then every category with its items and totals. */
export function ReportView({
  title,
  lines,
  unitId,
  onUnitChange,
}: {
  title: string;
  lines: GridLine[];
  unitId: number;
  onUnitChange: (id: number) => void;
}) {
  const [selected, setSelected] = useState<number | null>(null);
  const unit = unitById(unitId);
  const weighted = lines.map((l) => ({ category: l.item.category, grams: l.item.grams, quantity: l.quantity, worn: l.worn, consumable: l.consumable }));
  const cats = categorize(weighted);
  const total = cats.reduce((s, c) => s + c.grams, 0);
  const worn = cats.reduce((s, c) => s + c.worn, 0);
  const consumable = cats.reduce((s, c) => s + c.consumable, 0);
  const byCat = new Map<string, GridLine[]>();
  for (const l of lines) {
    const arr = byCat.get(l.item.category) ?? [];
    arr.push(l);
    byCat.set(l.item.category, arr);
  }
  return (
    <div className="ReportView shown">
      <h1>{title}</h1>
      <div className="homeLink">
        <a href="https://www.geargrams.com">GearGrams</a>
      </div>
      <div className="PieAndLegend">
        <div className="holder">
          <Pie categories={cats} width={300} height={300} widthOffset={70} heightOffset={70} selectedIndex={selected} onSelect={setSelected} onDeselect={() => setSelected(null)} />
          <Legend
            categories={cats}
            total={total}
            worn={worn}
            consumable={consumable}
            unitId={unitId}
            selectedIndex={selected}
            onSelect={setSelected}
            onDeselect={() => setSelected(null)}
            onUnitChange={onUnitChange}
          />
        </div>
      </div>
      <div className="gearList">
        <ul className="root">
          {cats.map((c) => (
            <li key={c.index}>
              <span className="categoryTitle">{categoryLabel(c.name)}</span>
              <ul className="category">
                {(byCat.get(c.name) ?? []).map((l) => (
                  <li key={l.key}>
                    <ItemName item={l.item} />
                    <span>{l.item.description}</span>
                    <span>{l.quantity}</span>
                    <span>{fmt(l.item.grams, unitById(l.item.unit_id))}</span>
                    <span>{l.worn && <img src="/assets/ui/icon/worn_on.png" alt="worn" />}</span>
                    <span>{l.consumable && <img src="/assets/ui/icon/consumable_on.png" alt="consumable" />}</span>
                  </li>
                ))}
                <li className="totals">
                  <span>&nbsp;</span> <span>&nbsp;</span>
                  <span>&nbsp;</span>
                  <span>
                    {inUnit(c.grams, unit)}
                    {unit.abbreviation}
                  </span>
                  <span>
                    {inUnit(c.worn, unit)}
                    {unit.abbreviation}
                  </span>
                  <span>
                    {inUnit(c.consumable, unit)}
                    {unit.abbreviation}
                  </span>
                </li>
              </ul>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function ItemName({ item }: { item: GearItem }) {
  return (
    <span title={item.name}>
      {item.url ? (
        <a href={item.url} target="_blank" rel="noreferrer">
          {item.name}
          <img className="link" src="/assets/ui/icon/link_on.png" alt="" />
        </a>
      ) : (
        item.name
      )}
    </span>
  );
}
